/**
 * E10a's own upload -> generate -> download flow, deliberately a new
 * page rather than a mode grafted onto GenerateJob.tsx (design spec
 * §3's own flagged point): a connector job takes one API-surface
 * document, not a source/destination schema pair, and its output is a
 * list of files, not one script.
 *
 * Multi-file download: one "Download" button per generated file,
 * rather than bundling them into a client-side zip — the simplest
 * correct option for the handful of files a connector job actually
 * produces, and it adds no new dependency (e.g. JSZip) for what a zip
 * would buy here.
 *
 * E10b — a format picker (OpenAPI / GraphQL introspection) plus the
 * endpoint path field GraphQL needs (introspection names no endpoint
 * of its own; api/parsers/graphql_introspection.py defaults it to
 * "/graphql" when left blank). Not a second route or a second page —
 * both formats share the exact same upload -> generate -> download
 * flow, only the document's own shape and one extra field differ.
 *
 * E12 — an optional platform picker (design spec §6 step 6), defaulting
 * to "no hint": seven curated platforms whose few-shot style notes
 * (api/providers/_saas_examples.py) sharpen generation for that
 * platform's own real auth/pagination quirks. Linear is GraphQL-only
 * but offered the same as the six REST platforms — nothing in this
 * picker or the generate call distinguishes a GraphQL platform hint
 * from a REST one; surfaceFormat alone decides which parser/prompt
 * pair runs.
 *
 * E11 web-portal design spec §1 — the result block (ready message,
 * pricing line, one download button per file, unresolved items) is
 * now GeneratedFilesResult, shared with WebhookJob.tsx: both pages
 * consume the identical GeneratedConnector response shape.
 */

import { useState, type ChangeEvent, type DragEvent } from 'react';

import { ApiError, generateConnector, type GeneratedConnector } from '../api/client';
import { GeneratedFilesResult } from '../components/GeneratedFilesResult';

type SurfaceFormat = 'openapi_surface' | 'graphql_introspection';

// E12 — the seven platforms providers/_saas_examples.py curates today,
// each paired with its own display label (avoids indexing into a
// string under noUncheckedIndexedAccess just to capitalize it).
const CURATED_PLATFORMS: Array<[value: string, label: string]> = [
  ['salesforce', 'Salesforce'],
  ['hubspot', 'HubSpot'],
  ['jira', 'Jira'],
  ['slack', 'Slack'],
  ['notion', 'Notion'],
  ['stripe', 'Stripe'],
  ['linear', 'Linear'],
];

export function ConnectorJob() {
  const [surfaceFormat, setSurfaceFormat] = useState<SurfaceFormat>('openapi_surface');
  const [endpointPath, setEndpointPath] = useState('');
  const [platform, setPlatform] = useState('');
  const [fileName, setFileName] = useState('');
  const [document_, setDocument] = useState<Record<string, unknown> | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<GeneratedConnector | null>(null);

  const isGraphql = surfaceFormat === 'graphql_introspection';
  const documentLabel = isGraphql
    ? 'GraphQL introspection result (JSON)'
    : 'OpenAPI document (JSON)';

  async function acceptFile(file: File) {
    setError('');
    setResult(null);
    const text = await file.text();
    try {
      const parsed = JSON.parse(text);
      setFileName(file.name);
      setDocument(parsed);
    } catch {
      setFileName('');
      setDocument(null);
      setError(`That file is not valid JSON — export your ${documentLabel} as JSON, not YAML.`);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) void acceptFile(file);
  }

  function handleFileInput(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void acceptFile(file);
  }

  async function generate() {
    if (!document_) return;
    setBusy(true);
    setError('');
    try {
      const generated = await generateConnector(document_, {
        format: surfaceFormat,
        endpointPath: isGraphql ? endpointPath : undefined,
        platform: platform || undefined,
      });
      setResult(generated);
    } catch (err) {
      setError(err instanceof ApiError ? (err.reason ?? err.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <h1>Generate an API connector</h1>
      <p className="muted">
        Drop an API surface document below. Nothing you upload is stored — the generated
        connector files are the only thing returned.
      </p>

      <label className="field-inline">
        <span>Source format</span>
        <select
          value={surfaceFormat}
          onChange={(event) => {
            setSurfaceFormat(event.target.value as SurfaceFormat);
            setFileName('');
            setDocument(null);
            setError('');
          }}
        >
          <option value="openapi_surface">OpenAPI</option>
          <option value="graphql_introspection">GraphQL (introspection JSON)</option>
        </select>
      </label>

      {isGraphql && (
        <label className="field-inline">
          <span>Endpoint path</span>
          <input
            type="text"
            placeholder="/graphql"
            value={endpointPath}
            onChange={(event) => setEndpointPath(event.target.value)}
          />
        </label>
      )}

      <label className="field-inline">
        <span>Platform (optional)</span>
        <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
          <option value="">No hint</option>
          {CURATED_PLATFORMS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <div
        className={`drop-box${dragOver ? ' drop-box--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <p className="drop-box-label">{documentLabel}</p>
        <label className="drop-box-input">
          {fileName || `Drag a ${documentLabel} file here, or click to browse`}
          <input type="file" accept=".json" onChange={handleFileInput} hidden />
        </label>
      </div>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <button
        className="btn btn--primary"
        disabled={!document_ || busy}
        onClick={() => void generate()}
      >
        {busy ? 'Generating…' : 'Generate connector'}
      </button>

      {result && <GeneratedFilesResult result={result} />}
    </main>
  );
}
